<template>
  <base-form v-if="orderLoaded"
             :title="order.name"
             :loading="loading"
             @submit="updateOrderPrice"
  >
    <template v-slot:header-right>
      <div v-if="order.status === orderStatuses.CHECK"
           class="flex">
        <base-button type="danger" outline @click="declineOrder">
          {{$t('order.edit.reject')}}
        </base-button>
        <base-button type="success"
                     outline
                     class="ml-2"
                     @click="verifyOrder">
          {{$t('order.edit.verify')}}
        </base-button>
      </div>
    </template>
    <template v-slot:content>
      <dl class="-mx-3">
        <div class="bg-white list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.orderDate')}}
          </dt>
          <dd class="list-row-dd">
            {{$formatDate(order.updated_at, FRIENDLY_DATETIME_FORMAT)}}
          </dd>
        </div>
        <div class="bg-gray-50 list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.product')}}
          </dt>
          <dd class="list-row-dd flex items-center">
            <el-tooltip :content="get(order, 'product.name', '')"
                        :open-delay="200"
                        placement="top"
                        effect="light">
              <base-avatar :src="get(order, 'product.image', '')"/>
            </el-tooltip>
            <span class="ml-2">{{get(order, 'product.name', $t('generic.na'))}}</span>
          </dd>
        </div>
        <div class="bg-white list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.related')}}
          </dt>
          <dd class="list-row-dd">
            <i class="nc-icon nc-tag-content"></i> &nbsp;
            <router-link :to="getRelatedLink(order.related)" @click.stop.prevent="navigateToRelated(order.related)">
              {{get(order, 'related.data.title', $t('generic.na'))}}
            </router-link>
          </dd>
        </div>
        <div class="bg-gray-50 list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.user')}}
          </dt>
          <dd class="list-row-dd flex items-center">
            <base-avatar :src="get(order, 'user.image', '')"/>
            <router-link :to="`/contacts/users/${get(order, 'user.uuid')}`" class="ml-2">
              {{get(order, 'user.given_name')}}
            </router-link>
          </dd>
        </div>
        <div class="bg-white list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.client')}}
          </dt>
          <dd class="list-row-dd flex items-center">
            <i class="nc-icon nc-briefcase-24"></i>&nbsp;
            <router-link :to="`/contacts/clients/${get(order, 'client.uuid')}`">
              {{get(order, 'client.name')}}
            </router-link>
          </dd>
        </div>
        <div class="bg-gray-50 list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.invoice')}}
          </dt>
          <dd class="list-row-dd">
            {{getInvoiceName(order)}}
          </dd>
        </div>
        <div class="bg-white list-row">
          <dt class="list-row-dt">
            {{$t('order.list.labels.status')}}
          </dt>
          <dd class="list-row-dd">
            <status-badge :status="order.status"/>
          </dd>
        </div>
      </dl>

      <div class="px-3 mt-5">
        <fg-input v-model="order.price"
                  type="number"
                  :name="$t('generic.price')"
                  :label="$t('generic.price')"
                  :placeholder="$t('generic.price')"
                  :error="getError($t('generic.price'))"
        >
        </fg-input>
      </div>
    </template>
  </base-form>
</template>

<script>
  import get from 'lodash/get'
  import { orderStatuses } from 'src/config/orderStatuses'
  import OrderService from 'src/services/OrderService'
  import { FRIENDLY_DATETIME_FORMAT } from 'src/config/dateUtils'

  export default {
    data () {
      return {
        orderStatuses,
        FRIENDLY_DATETIME_FORMAT,
        order: {},
        orderLoaded: false,
        loading: false,
        showPriceModal: false,
        orderStatusTextMappings: {
          [orderStatuses.SUCCESS]: 'text-success',
          [orderStatuses.PAYMENT_PROVIDER]: 'text-success',
          [orderStatuses.REJECTED]: 'text-danger',
          [orderStatuses.CHECK]: 'text-info',
          [orderStatuses.CART]: 'text-info',
          [orderStatuses.EXPIRED]: 'text-danger',
        },
      }
    },
    computed: {
      orderStatusMessages () {
        return {
          [orderStatuses.SUCCESS]: this.$t('order.statuses.success'),
          [orderStatuses.REJECTED]: this.$t('order.statuses.rejected'),
          [orderStatuses.CHECK]: this.$t('order.statuses.check'),
          [orderStatuses.CART]: this.$t('order.statuses.cart'),
          [orderStatuses.PAYMENT_PROVIDER]: this.$t('order.statuses.paymentProvider'),
          [orderStatuses.EXPIRED]: this.$t('order.statuses.expired'),
        }
      }
    },
    methods: {
      get,
      async verifyOrder () {
        await OrderService.orderVerify(this.order.uuid, {status: orderStatuses.SUCCESS})
      },
      async declineOrder () {
        await OrderService.orderVerify(this.order.uuid, {status: orderStatuses.REJECTED})
      },
      getInvoiceName(row) {
        return get(row, 'invoice.name', this.$t('generic.na'))
      },
      navigateToRelated (related) {
        this.$router.push(this.getRelatedLink(related))
      },
      getRelatedLink (related) {
        if (!related) {
          return
        }
        let data = related.data
        if (!related.entity) {
          return ''
        }
        let path = `/${related.entity}s`
        if (data.uuid) {
          path += `/${data.uuid}`
        }
        return path
      },
      async updateOrderPrice() {
        try {
          this.loading = true
          await OrderService.updateOrderPrice(this.order.uuid, {
            price: this.order.price
          })
        } finally {
          this.loading = false
        }
      }
    },
    async mounted () {
      let id = this.$route.params.id
      let { data } = await OrderService.getById(id, {
        with: 'client,user,invoice,product'
      })
      this.orderLoaded = true
      this.order = data
    }
  }
</script>

<style lang="scss" scoped>
  .order__description {
    display: flex;
    justify-content: center;
    padding-left: 15px;
    margin-top: -15px;
  }

  .order__description h6 {
    text-transform: none;
  }

  .order__info {
    margin: 10px 0;
  }
</style>
